import { getActiveRotatorGizmoRoot } from '@utils/sceneObjects/items/pivotGizmoRegistry'
import { Intersection, Object3D, Raycaster, Vector3 } from 'three'
import { Line2, LineMaterial } from 'three-stdlib'

const ROTATOR_PICK_KEY = '__rotator_pick_layer__'
const ROTATOR_ARC_PICK_PX = 40
const NEIGHBOR_ARC_PICK_PX = 56
const ROTATOR_ARC_MAX_WORLD_M = 0.75
const NEIGHBOR_ARC_DEFER_M = 0.15
const NEIGHBOR_ARC_DEFER_MAX_DIST_M = 1.6

export function getPivotGizmoSiblingGroup(contentRoot: Object3D): Object3D | null {
  const userGroup = contentRoot.parent
  const pivotGroup = userGroup?.parent
  if (!pivotGroup || pivotGroup.children.length < 2) return null
  return pivotGroup.children[0] ?? null
}

export function isPivotControlHit(object: Object3D | null | undefined): boolean {
  let current = object ?? null
  while (current) {
    if (current.userData?.pivotControlsVisible) return true
    current = current.parent
  }
  return false
}

export function eventTargetsPivotGizmo(intersections: Intersection[] | undefined): boolean {
  return (intersections ?? []).some((hit) => isPivotControlHit(hit.object))
}

function isPivotRotatorLine2(line: Line2): boolean {
  const parent = line.parent
  if (!parent) return false
  const line2s = parent.children.filter((c) => c instanceof Line2)
  if (line2s.length !== 2) return false
  const geom = line.geometry as { instanceCount?: number }
  const segCount = geom?.instanceCount ?? 0
  return segCount >= 16 && segCount <= 40
}

function withLine2PickThreshold(raycaster: Raycaster, extraPx: number, fn: () => void) {
  const line2Params = raycaster.params.Line2 ?? (raycaster.params.Line2 = { threshold: 0 })
  const previous = line2Params.threshold ?? 0
  line2Params.threshold = Math.max(previous, extraPx)
  try {
    fn()
  } finally {
    line2Params.threshold = previous
  }
}

function enableRotatorInvisiblePickLayer(line: Line2) {
  const tagged = line as Line2 & { [ROTATOR_PICK_KEY]?: boolean }
  if (tagged[ROTATOR_PICK_KEY]) return
  line.visible = true
  const material = line.material as LineMaterial
  material.transparent = true
  material.opacity = 0
  material.depthWrite = false
  material.depthTest = true
  tagged[ROTATOR_PICK_KEY] = true
}

function trimArcHitsFarFromGizmo(
  intersects: Intersection[],
  fromIndex: number,
  gizmoRoot: Object3D,
) {
  const anchor = new Vector3()
  gizmoRoot.getWorldPosition(anchor)
  for (let i = intersects.length - 1; i >= fromIndex; i--) {
    if (intersects[i].point.distanceTo(anchor) > ROTATOR_ARC_MAX_WORLD_M) {
      intersects.splice(i, 1)
    }
  }
}

function bindRotatorArcPick(line: Line2, gizmoRoot: Object3D) {
  const original = Line2.prototype.raycast.bind(line)
  line.raycast = function raycast(raycaster, intersects) {
    const before = intersects.length
    withLine2PickThreshold(raycaster, ROTATOR_ARC_PICK_PX, () => {
      original(raycaster, intersects)
    })
    trimArcHitsFarFromGizmo(intersects, before, gizmoRoot)
  }
}

function closestHitDistance(hits: Intersection[]): number | null {
  if (hits.length === 0) return null
  let min = hits[0].distance
  for (let i = 1; i < hits.length; i++) {
    if (hits[i].distance < min) min = hits[i].distance
  }
  return min
}

function raycastRotatorArcs(
  gizmoRoot: Object3D,
  raycaster: Raycaster,
  target: Intersection[],
  useNeighborBoost: boolean,
) {
  const run = () => {
    gizmoRoot.traverse((child) => {
      if (child instanceof Line2 && isPivotRotatorLine2(child)) {
        if (useNeighborBoost && typeof child.raycast === 'function') {
          child.raycast(raycaster, target)
        } else {
          Line2.prototype.raycast.call(child, raycaster, target)
        }
      }
    })
  }
  if (useNeighborBoost) {
    withLine2PickThreshold(raycaster, NEIGHBOR_ARC_PICK_PX, run)
  } else {
    run()
  }
}

function collectGizmoHitsForDrag(
  gizmoRoot: Object3D,
  raycaster: Raycaster,
  target: Intersection[],
) {
  gizmoRoot.traverse((child) => {
    if (child instanceof Line2 && isPivotRotatorLine2(child)) {
      Line2.prototype.raycast.call(child, raycaster, target)
      return
    }
    if (!child.visible) return
    if (typeof child.raycast !== 'function') return
    child.raycast(raycaster, target)
  })
}

export function enablePivotGizmoLineRaycasts(gizmoRoot: Object3D) {
  gizmoRoot.traverse((child) => {
    if (child instanceof Line2 && isPivotRotatorLine2(child)) {
      enableRotatorInvisiblePickLayer(child)
      bindRotatorArcPick(child, gizmoRoot)
      return
    }
    if (child instanceof Line2) {
      child.raycast = Line2.prototype.raycast.bind(child)
    }
  })
}

export function dragColliderRaycastPreferPivotGizmo(
  this: Object3D,
  raycaster: Raycaster,
  intersects: Intersection[],
  gizmoRoot: Object3D | null,
  originalRaycast: Object3D['raycast'],
) {
  const gizmoHits: Intersection[] = []
  if (gizmoRoot) collectGizmoHitsForDrag(gizmoRoot, raycaster, gizmoHits)
  const gizmoDistance = closestHitDistance(gizmoHits)

  const bodyHits: Intersection[] = []
  originalRaycast.call(this, raycaster, bodyHits)
  const bodyDistance = closestHitDistance(bodyHits)

  if (gizmoDistance != null && bodyDistance != null && gizmoDistance <= bodyDistance) return

  intersects.push(...bodyHits)
}

export function meshRaycastDeferActiveUnitRotator(
  this: Object3D,
  raycaster: Raycaster,
  intersects: Intersection[],
  originalRaycast: Object3D['raycast'],
) {
  const activeGizmo = getActiveRotatorGizmoRoot()
  if (!activeGizmo) {
    originalRaycast.call(this, raycaster, intersects)
    return
  }

  const bodyHits: Intersection[] = []
  originalRaycast.call(this, raycaster, bodyHits)
  if (bodyHits.length === 0) return

  const gizmoWorld = new Vector3()
  activeGizmo.getWorldPosition(gizmoWorld)
  if (bodyHits[0].point.distanceTo(gizmoWorld) > NEIGHBOR_ARC_DEFER_MAX_DIST_M) {
    intersects.push(...bodyHits)
    return
  }

  const bodyDistance = closestHitDistance(bodyHits)
  const rotatorHits: Intersection[] = []
  raycastRotatorArcs(activeGizmo, raycaster, rotatorHits, true)
  if (rotatorHits.length === 0) {
    intersects.push(...bodyHits)
    return
  }

  const rotatorDistance = closestHitDistance(rotatorHits)
  if (
    rotatorDistance != null &&
    bodyDistance != null &&
    rotatorDistance <= bodyDistance + NEIGHBOR_ARC_DEFER_M
  ) {
    return
  }

  intersects.push(...bodyHits)
}

export const ITEM_DRAG_COLLIDER_NAME = '__item_drag_collider__'
